import { reduxStore } from 'index';
import { get, set } from 'global/lib/lodash';
import axios from 'axios';
import config from 'config';
import { wait } from 'global/utils';
import normalize from 'json-api-normalizer';
import build from 'redux-object';
import qs from 'qs';
import pako from 'pako';
import { SHARE_HASH_API_NAMESPACE } from 'feature/share/shareLinkConfig';

const gzipSendEnabled = false;

export const client = axios.create({
  // baseURL: `${config.api.baseUrl}${config.api.jsonNamespace}`,
  responseType: 'json',
  timeout: 60000,
  transformRequest: axios.defaults.transformRequest.concat(function (data, headers) {
    // compress strings if over 1KB
    if (typeof data === 'string' && data.length > 1024 && gzipSendEnabled) {
      headers['Content-Encoding'] = 'gzip';
      return pako.gzip(data);
    } else {
      // delete is slow apparently, faster to set to undefined
      headers['Content-Encoding'] = undefined;
      return data;
    }
  }),
});

const defaults = {
  useNamespace: true,
  useContentType: 'application/vnd.api+json',
  delay: 2000,
  tries: 0,
  maxTries: 3,
  simulate: false,
};

const formatError = (err) => {
  const { data } = get(err, 'response', {});
  const hasErrResp = get(data, 'message');
  const hasErrorTitle = get(data, 'errors.0.title');

  if (hasErrResp) {
    const { message, exception, file } = data;
    // When the backend only sends `message` (e.g. an expired share hash returns
    // 401 { message: 'Invalid or expired hash.' }), don't append the diagnostic
    // suffix — that produced the malformed "<message>: undefined\nFile: undefined".
    if (!exception && !file) {
      return message;
    }
    return `${message}: ${exception}\nFile: ${file}`;
  } else if (hasErrorTitle) {
    const { title, status } = get(data, 'errors.0');
    return `${!!status ? `Request failed with status code ${status} (${title})` : title}`;
  } else {
    return err.message;
  }
};

export const getBaseUrl = (url = '', opts) => {
  const durl = url.startsWith('/') ? url.substring(1) : url;
  // PFM-2547: hashed share-link sessions live in a separate JSON:API namespace.
  // When opts.useShareHashNamespace is set (and the caller asked for a namespace),
  // swap /api/v1/ for /sharehash/v1/.
  const namespace = opts.useNamespace
    ? (opts.useShareHashNamespace ? SHARE_HASH_API_NAMESPACE : config.api.jsonNamespace)
    : '';
  return `${config.api.baseUrl}${namespace}${durl}`;
};

export const sendData = async (data, _opts = {}) => {
  const opts = {
    ...defaults,
    ..._opts,
  };

  try {
    const { store } = reduxStore;
    const sState = store.getState();

    // PFM-2547: when a hashed share-link session is active, route reads to the
    // share-hash bearer + /sharehash/v1 namespace. The hash token is only stored
    // in state when the current user does NOT have normal access to the
    // resource (redeemHash sets hashToken=null when hasNormalAccess === true),
    // so the presence of hashToken is sufficient to switch sessions — even if
    // the user also holds a normal accessToken for an org without access to
    // this content. Pinned to the single shared resource by HashViewGuard.
    const hashToken = get(sState, 'hashAuth.hashToken');
    const userToken = get(sState, 'auth.accessToken');
    const useHashSession = !!hashToken;
    const bearerToken = useHashSession ? hashToken : userToken;
    opts.useShareHashNamespace = useHashSession;

    const headers = {
      Authorization: 'Bearer ' + bearerToken,
      ...get(data, 'headers', {}),
    };

    if (opts.useAuth === false) {
      delete headers.Authorization;
    }

    if (opts.useContentType) {
      set(headers, 'Content-Type', opts.useContentType);
    }

    const buildURL = () => {
      let url = getBaseUrl(data.url, opts);

      if (opts.params && Object.keys(opts.params).length > 0) {
        url += `?${qs.stringify(opts.params)}`;
      }
      if (opts.version) {
        url = url.replace('/v1/', `/${opts.version}/`);
      }
      return url;
    };

    if (opts.simulate) {
      console.log({
        ...data,
        headers,
      });
      await wait(opts.delay);
      return { data: {} };
    } else {
      const params = {
        ...data,
        url: buildURL(),
        headers,
      };
      if (opts.log) {
        console._log('REQUEST LOG', params);
      }

      if (opts.simulateFail) {
        await wait();
        throw new Error('Simulated Fail');
      }

      const resp = await client.request(params);

      if (opts.normalize) {
        const d = normalize(resp.data);
        const built = build(d, get(opts, 'normalize.build.0'), get(opts, 'normalize.build.1'));
        return built;
      } else {
        return resp;
      }
    }
  } catch (err) {
    console.error(err);
    const { status } = get(err, 'response', {});
    if (status >= 400 && status < 500) {
      opts.tries = opts.maxTries;
    }
    if (opts.tries < opts.maxTries) {
      await wait();
      return sendData(data, {
        ...opts,
        tries: opts.tries + 1,
      });
    } else {
      const error = formatError(err);
      err.message = error;
      throw err;
    }
  }
};

export default sendData;
